import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { offeringSessionRuleSetSchema } from '../../_validator';

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  CardDescription
} from '@/components/ui/card';
import SpinnerButton from '@/components/common/SpinnerButton';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';

import Error from '@/components/common/Error';

import { useOffering } from '@/data/offering';
import {
  useOfferingSessionRuleSets,
  usePostOfferingSessionRuleSet
} from '@/data/offeringSessionRuleSet';

import FormGeneral from '../common/FormGeneral';

const AddOfferingSessionRuleSetForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const offering = useOffering(params.offeringId);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [formError, setFormError] = useState({});
  const [timeSlotErrorMessage, setTimeSlotErrorMessage] = useState('');
  const postOfferingSessionRuleSet = usePostOfferingSessionRuleSet();
  const offeringSessionRuleSets = useOfferingSessionRuleSets(params.offeringId);
  const form = useForm({
    resolver: zodResolver(offeringSessionRuleSetSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      offeringSessionRules: []
    }
  });

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      setTimeSlotErrorMessage('');
      const payload = {
        ...values,
        offeringId: params.offeringId
      };
      await postOfferingSessionRuleSet(payload);
      await offeringSessionRuleSets.mutate();
      navigate(`/offering?coachActivityId=${offering.data.coachActivityId}`);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err) => {
    setFormError(err);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add Availability</CardTitle>
        <CardDescription>
          Add a date range for your seasonal availability for{' '}
          {offering.data.name}. Set the days of the week you are available for.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, handleError)}>
          <CardContent>
            <FormGeneral
              setFormError={setFormError}
              timeSlotErrorMessage={timeSlotErrorMessage}
              setTimeSlotErrorMessage={setTimeSlotErrorMessage}
            />
            {apiError && <Error error={apiError} />}
            {formError.offeringSessionRules && (
              <Error error={formError.offeringSessionRules.message} />
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Link
              to={`/offering?coachActivityId=${offering.data.coachActivityId}`}
            >
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <SpinnerButton loading={submitting} type="submit">
              Add Availability
            </SpinnerButton>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default AddOfferingSessionRuleSetForm;
